<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  companyId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['addContact']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const isLoading = ref(false);
const contacts = ref([]);

const fetchContacts = async () => {
  isLoading.value = true;
  try {
    const response = await store.dispatch('companies/getContacts', {
      companyId: props.companyId,
    });
    contacts.value = response || [];
  } catch (error) {
    contacts.value = [];
  } finally {
    isLoading.value = false;
  }
};

const goToContact = contactId => {
  router.push({
    name: 'contacts_edit',
    params: {
      accountId: route.params.accountId,
      contactId,
    },
  });
};

const addNewContact = () => {
  emit('addContact');
};

const getContactChannelIcon = contact => {
  if (contact.email) return 'i-lucide-mail';
  if (contact.phone_number) return 'i-lucide-phone';
  return 'i-lucide-user';
};

const getContactInfo = contact => {
  if (contact.email) return contact.email;
  if (contact.phone_number) return contact.phone_number;
  return null;
};

onMounted(() => {
  fetchContacts();
});

defineExpose({ fetchContacts });
</script>

<template>
  <div class="flex flex-col h-full px-6">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('COMPANY_DETAILS.SIDEBAR.CONTACTS.TITLE') }}
        <span v-if="contacts.length > 0" class="ml-1 text-xs text-n-slate-10">
          ({{ contacts.length }})
        </span>
      </h3>
      <Button icon="i-lucide-user-plus" size="xs" @click="addNewContact" />
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>

    <div
      v-else-if="contacts.length === 0"
      class="flex flex-col items-center justify-center py-8 text-center"
    >
      <Icon icon="i-lucide-users" size="size-8 text-n-slate-9 mb-2" />
      <span class="text-sm text-n-slate-11">
        {{ t('COMPANY_DETAILS.SIDEBAR.CONTACTS.EMPTY') }}
      </span>
      <Button
        :label="t('COMPANY_DETAILS.SIDEBAR.CONTACTS.ADD_FIRST')"
        size="sm"
        class="mt-3"
        @click="addNewContact"
      />
    </div>

    <div v-else class="flex flex-col gap-2 overflow-y-auto">
      <div
        v-for="contact in contacts"
        :key="contact.id"
        class="flex items-center gap-3 p-3 transition-colors border rounded-lg cursor-pointer border-n-weak hover:bg-n-alpha-2"
        @click="goToContact(contact.id)"
      >
        <Avatar
          :name="contact.name || contact.email || ''"
          :username="contact.name || contact.email"
          :src="contact.thumbnail"
          :size="36"
          rounded-full
        />
        <div class="flex flex-col flex-1 min-w-0">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ contact.name || t('COMPANY_DETAILS.SIDEBAR.CONTACTS.UNNAMED') }}
          </span>
          <div
            v-if="getContactInfo(contact)"
            class="flex items-center gap-1.5 text-xs text-n-slate-11"
          >
            <Icon :icon="getContactChannelIcon(contact)" size="size-3" />
            <span class="truncate">{{ getContactInfo(contact) }}</span>
          </div>
        </div>
        <Icon icon="i-lucide-chevron-right" size="size-4 text-n-slate-10" />
      </div>
    </div>
  </div>
</template>
